import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { motion, useInView, animate } from 'framer-motion';
import { Trophy, Users, CalendarDays, Target, Flag, ArrowLeft, ArrowRight, Newspaper, Clock, Shirt, Shield } from 'lucide-react';

const reveal = {
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

let extrasPromise = null;
const useHomeExtras = (API) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    extrasPromise = extrasPromise || axios.get(`${API}/public/home-extras`).then((r) => r.data).catch(() => null);
    extrasPromise.then(setData);
  }, [API]);
  return data;
};

const fileUrl = (API, v) => !v ? null : v.startsWith('http') ? v : v.startsWith('/api') ? `${API.replace(/\/api$/, '')}${v}` : `${API}/files/${v}`;
const newsImage = (API, n) => fileUrl(API, n.images?.[0] || n.image_url || n.image_file_id);
// Shows the full image (no cropping) over a blurred copy of itself
const FitImage = ({ src, className = '', hover = true }) => (
  <div className={`relative overflow-hidden bg-slate-900 ${className}`}>
    <img src={src} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-60" />
    <img src={src} alt="" className={`relative w-full h-full object-contain ${hover ? 'transition-transform duration-500 group-hover:scale-[1.03]' : ''}`} />
  </div>
);

const formatDate = (d) => d ? new Date(d).toLocaleDateString('ar-SA', { day: 'numeric', month: 'long' }) : '';

const SectionHead = ({ kicker, title, action }) => (
  <div className="flex items-end justify-between gap-4 mb-8 lg:mb-10">
    <div>
      <div className="flex items-center gap-2 mb-2">
        <span className="h-[3px] w-8 rounded-full bg-orange-500" />
        <span className="text-xs font-bold tracking-wide text-orange-600">{kicker}</span>
      </div>
      <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900">{title}</h2>
    </div>
    {action}
  </div>
);

const Counter = ({ to }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const c = animate(0, to, { duration: 1.6, ease: 'easeOut', onUpdate: (v) => setVal(Math.round(v)) });
    return () => c.stop();
  }, [inView, to]);
  return <span ref={ref}>{val.toLocaleString('en-US')}</span>;
};

export const ClubStatsBand = ({ API }) => {
  const data = useHomeExtras(API);
  if (!data) return null;
  const s = data.stats;
  const items = [
    { icon: Flag, value: s.years, label: 'عاماً من التاريخ', sub: `منذ ${s.founded_year}` },
    { icon: Trophy, value: s.achievements, label: 'إنجاز وبطولة' },
    { icon: Users, value: s.players, label: 'لاعب مسجّل' },
    { icon: CalendarDays, value: s.matches_played, label: 'مباراة لُعبت' },
    { icon: Target, value: s.goals_for, label: 'هدف سجّلناه' },
  ];
  return (
    <section data-testid="club-stats-band" className="relative bg-[#0B1220] overflow-hidden" dir="rtl">
      <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: 'repeating-linear-gradient(135deg, #F97316 0 1px, transparent 1px 14px)' }} />
      <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full bg-orange-500/20 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-12 lg:py-16 grid grid-cols-2 md:grid-cols-5 gap-6 lg:gap-8">
        {items.map((it, i) => (
          <motion.div key={it.label} {...reveal} transition={{ ...reveal.transition, delay: i * 0.08 }} className={`text-center md:text-right ${i === 0 ? 'col-span-2 md:col-span-1' : ''}`}>
            <it.icon className="w-6 h-6 text-orange-400 mb-3 mx-auto md:mx-0" strokeWidth={1.8} />
            <div data-testid={`club-stat-${i}`} className="text-4xl lg:text-5xl font-black text-white tabular-nums"><Counter to={it.value || 0} /></div>
            <div className="text-sm text-slate-300 font-semibold mt-1">{it.label}</div>
            {it.sub && <div className="text-xs text-orange-300/80 mt-0.5">{it.sub}</div>}
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export const NewsMagazine = ({ API, onOpenNews }) => {
  const [news, setNews] = useState([]);
  useEffect(() => {
    axios.get(`${API}/public/news`, { params: { limit: 7 } }).then((r) => setNews(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    // Deep link from shared news link: /?news=<id>
    const sharedId = new URLSearchParams(window.location.search).get('news');
    if (sharedId) axios.get(`${API}/public/news/${sharedId}`).then((r) => onOpenNews(r.data)).catch(() => {});
  }, [API]);
  if (!news.length) return null;
  const [lead, ...rest] = news;
  const leadImg = newsImage(API, lead);
  return (
    <section id="news-magazine" data-testid="news-magazine" className="bg-[#FAFAF9] py-16 lg:py-24" dir="rtl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <SectionHead kicker="المركز الإعلامي" title="آخر الأخبار" />
        <div className="grid lg:grid-cols-5 gap-6">
          <motion.button {...reveal} data-testid="news-lead-card" onClick={() => onOpenNews(lead)} className="lg:col-span-3 group relative rounded-3xl overflow-hidden min-h-[340px] lg:min-h-[480px] text-right bg-slate-900">
            {leadImg && <FitImage src={leadImg} className="absolute inset-0" />}
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
            <div className="absolute bottom-0 p-6 lg:p-10">
              <span className="inline-flex items-center gap-1.5 bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full mb-4"><Newspaper className="w-3.5 h-3.5" /> الخبر الرئيسي</span>
              <h3 className="text-2xl lg:text-4xl font-black text-white leading-snug mb-3">{lead.title}</h3>
              {lead.summary && <p className="text-slate-200 text-sm lg:text-base line-clamp-2 max-w-2xl">{lead.summary}</p>}
              <div className="flex items-center gap-2 text-slate-300 text-xs mt-4"><Clock className="w-3.5 h-3.5" />{formatDate(lead.published_at || lead.created_at)}</div>
            </div>
          </motion.button>
          <div className="lg:col-span-2 grid gap-4 content-start">
            {rest.slice(0, 4).map((n, i) => {
              const img = newsImage(API, n);
              return (
                <motion.button key={n.id || i} {...reveal} transition={{ ...reveal.transition, delay: 0.1 + i * 0.08 }} data-testid={`news-card-${i}`} onClick={() => onOpenNews(n)} className="group flex gap-4 items-center bg-white rounded-2xl p-3 border border-slate-100 hover:border-orange-200 hover:shadow-lg hover:-translate-y-0.5 transition-[box-shadow,transform,border-color] text-right">
                  <div className="w-28 h-24 sm:w-32 shrink-0 rounded-xl overflow-hidden bg-orange-50">
                    {img ? <FitImage src={img} className="w-full h-full" /> : <div className="w-full h-full flex items-center justify-center"><Newspaper className="w-8 h-8 text-orange-300" /></div>}
                  </div>
                  <div className="min-w-0">
                    <h4 className="font-bold text-slate-900 leading-snug line-clamp-2 group-hover:text-orange-600 transition-colors">{n.title}</h4>
                    <div className="flex items-center gap-1.5 text-slate-400 text-xs mt-2"><Clock className="w-3 h-3" />{formatDate(n.published_at || n.created_at)}</div>
                  </div>
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

const RESULT = { win: ['فوز', 'bg-emerald-500'], draw: ['تعادل', 'bg-slate-400'], loss: ['خسارة', 'bg-rose-500'] };

const TeamMark = ({ API, name, fileId, url }) => {
  const src = fileUrl(API, fileId) || fileUrl(API, url);
  return (
    <div className="flex flex-col items-center gap-2 w-24">
      <div className="w-14 h-14 rounded-full bg-slate-50 border border-slate-100 flex items-center justify-center overflow-hidden">
        {src ? <img src={src} alt="" className="w-11 h-11 object-contain" /> : <Shield className="w-6 h-6 text-slate-300" />}
      </div>
      <span className="text-xs font-bold text-slate-700 text-center line-clamp-1">{name}</span>
    </div>
  );
};

export const MatchCenter = ({ API }) => {
  const data = useHomeExtras(API);
  if (!data || !data.recent_results.length) return null;
  return (
    <section data-testid="match-center" className="bg-white py-16 lg:py-24" dir="rtl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <SectionHead kicker={data.season ? `موسم ${data.season}` : 'الموسم الحالي'} title="آخر المباريات" />
        <div className="grid md:grid-cols-3 gap-5">
          {data.recent_results.map((m, i) => (
            <motion.div key={m.id || i} {...reveal} transition={{ ...reveal.transition, delay: i * 0.08 }} data-testid={`result-card-${i}`} className="group relative rounded-3xl border border-slate-100 bg-[#FAFAF9] p-6 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-[box-shadow,transform]">
              <div className={`absolute top-0 inset-x-0 h-1 ${RESULT[m.result][1]}`} />
              <div className="flex items-center justify-between gap-2 mb-6">
                <span data-testid={`result-category-${i}`} className="inline-flex items-center gap-1.5 text-xs font-bold text-orange-700 bg-orange-100 px-3 py-1 rounded-full"><Users className="w-3.5 h-3.5" />{m.category || 'الفريق الأول'}</span>
                <span className="text-xs text-slate-400 font-semibold flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" />{formatDate(m.match_date)}</span>
              </div>
              <div className="flex items-center justify-between">
                <TeamMark API={API} name={m.home_team} fileId={m.home_logo_file_id} url={m.home_logo_url} />
                <div className="flex flex-col items-center gap-2">
                  <div className="flex items-center gap-2 text-4xl font-black text-slate-900 tabular-nums"><span>{m.home_score}</span><span className="text-slate-300">-</span><span>{m.away_score}</span></div>
                  <span className={`text-[11px] font-bold text-white px-2.5 py-0.5 rounded-full ${RESULT[m.result][1]}`}>{RESULT[m.result][0]}</span>
                </div>
                <TeamMark API={API} name={m.away_team} fileId={m.away_logo_file_id} url={m.away_logo_url} />
              </div>
              {m.tournament && <div className="mt-5 pt-4 border-t border-slate-100 text-center text-xs font-semibold text-slate-500 flex items-center justify-center gap-1.5"><Trophy className="w-3.5 h-3.5 text-amber-500" />{m.tournament}</div>}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const PlayersStrip = ({ API }) => {
  const data = useHomeExtras(API);
  const scroller = useRef(null);
  if (!data || !data.stars.length) return null;
  const scrollBy = (dir) => scroller.current?.scrollBy({ left: dir * 480, behavior: 'smooth' });
  return (
    <section data-testid="players-strip" className="bg-[#FAFAF9] py-16 lg:py-24 overflow-hidden" dir="rtl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <SectionHead kicker="الفريق الأول" title="نجوم الأمجاد" action={
          <div className="flex items-center gap-3">
            <a href="/players" data-testid="players-strip-all-link" className="group flex items-center gap-2 text-sm font-bold text-orange-600 hover:text-orange-700">كل اللاعبين <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" /></a>
            <div className="hidden sm:flex gap-2">
              <button data-testid="players-strip-prev-btn" onClick={() => scrollBy(1)} className="w-10 h-10 rounded-full border border-slate-200 bg-white hover:bg-orange-500 hover:text-white hover:border-orange-500 transition-colors flex items-center justify-center"><ArrowRight className="w-4 h-4" /></button>
              <button data-testid="players-strip-next-btn" onClick={() => scrollBy(-1)} className="w-10 h-10 rounded-full border border-slate-200 bg-white hover:bg-orange-500 hover:text-white hover:border-orange-500 transition-colors flex items-center justify-center"><ArrowLeft className="w-4 h-4" /></button>
            </div>
          </div>
        } />
        <div ref={scroller} className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory -mx-4 px-4 sm:mx-0 sm:px-0" style={{ scrollbarWidth: 'none' }}>
          {data.stars.map((p, i) => (
            <motion.div key={p.id} {...reveal} transition={{ ...reveal.transition, delay: Math.min(i, 6) * 0.06 }} data-testid={`player-card-${i}`} className="group snap-start shrink-0 w-48 sm:w-56 rounded-3xl overflow-hidden bg-gradient-to-b from-orange-500 to-orange-700 relative">
              {p.photo_url && <div className="absolute top-3 left-4 text-6xl font-black text-white/20 tabular-nums">{p.jersey_number ?? ''}</div>}
              <div className="h-60 sm:h-64 flex items-end justify-center">
                {p.photo_url ? <img src={fileUrl(API, p.photo_url)} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105" /> : (
                  <div className="relative mb-16 flex items-center justify-center">
                    <Shirt className="w-32 h-32 text-white/25" strokeWidth={1.2} />
                    <span className="absolute text-3xl font-black text-white/90 tabular-nums">{p.jersey_number ?? p.name?.charAt(0)}</span>
                  </div>
                )}
              </div>
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/85 to-transparent p-4 pt-10">
                <div className="text-white font-bold truncate">{p.name}</div>
                <div className="flex items-center justify-between text-xs text-orange-200 mt-0.5">
                  <span>{p.position || 'لاعب'}</span>
                  {p.total_goals > 0 && <span className="font-bold">{p.total_goals} هدف</span>}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const XLogo = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
);

export const XNewsSection = ({ API, onOpenNews, xUrl }) => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    axios.get(`${API}/public/news`, { params: { limit: 6, source: 'twitter' } }).then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [API]);
  if (!items.length && !xUrl) return null;
  return (
    <section data-testid="x-news-section" className="bg-white py-16 lg:py-20" dir="rtl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <SectionHead kicker="من حسابنا على X" title="آخر المستجدات" action={xUrl && (
          <a href={xUrl} target="_blank" rel="noopener noreferrer" data-testid="x-follow-btn" className="flex items-center gap-2 bg-black hover:bg-slate-800 text-white text-sm font-bold px-4 py-2.5 rounded-full transition-colors"><XLogo className="w-4 h-4" /> تابعنا</a>
        )} />
        {items.length ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map((n, i) => {
              const img = newsImage(API, n);
              return (
                <motion.button key={n.id || i} {...reveal} transition={{ ...reveal.transition, delay: i * 0.06 }} data-testid={`x-news-card-${i}`} onClick={() => onOpenNews(n)} className="group text-right rounded-2xl border border-slate-100 bg-[#FAFAF9] overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-[box-shadow,transform]">
                  {img && <FitImage src={img} className="h-56" />}
                  <div className="p-5">
                    <div className="flex items-center gap-2 text-xs text-slate-400 mb-2"><XLogo className="w-3 h-3 text-slate-900" />{formatDate(n.published_at || n.created_at)}</div>
                    <h4 className="font-bold text-slate-900 leading-snug line-clamp-2 group-hover:text-orange-600 transition-colors">{n.title}</h4>
                    {n.summary && <p className="text-sm text-slate-500 mt-2 line-clamp-2">{n.summary}</p>}
                  </div>
                </motion.button>
              );
            })}
          </div>
        ) : (
          <a href={xUrl} target="_blank" rel="noopener noreferrer" className="block rounded-3xl bg-black text-white p-8 lg:p-12 text-center hover:bg-slate-900 transition-colors">
            <XLogo className="w-10 h-10 mx-auto mb-4" />
            <p className="text-lg font-bold">تابع آخر أخبار النادي لحظة بلحظة على X</p>
          </a>
        )}
      </div>
    </section>
  );
};

export const StrategySection = ({ API }) => {
  const [st, setSt] = useState(null);
  useEffect(() => {
    axios.get(`${API}/public/strategy`).then((r) => setSt(r.data)).catch(() => {});
  }, [API]);
  if (!st || (!st.vision && !st.mission)) return null;
  const hero = st.hero_image_file_id ? `${API}/files/${st.hero_image_file_id}` : null;
  return (
    <section id="strategy" data-testid="strategy-section" className="relative bg-[#0B1220] text-white py-16 lg:py-24 overflow-hidden" dir="rtl">
      {hero && <img src={hero} alt="" className="absolute inset-0 w-full h-full object-cover opacity-15" />}
      <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-orange-500/20 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-end justify-between gap-4 mb-10">
          <div>
            <div className="flex items-center gap-2 mb-2"><span className="h-[3px] w-8 rounded-full bg-orange-500" /><span className="text-xs font-bold text-orange-400">استراتيجية النادي</span></div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black">نحو مستقبل أكثر إشراقاً</h2>
          </div>
          <a href="/strategy" data-testid="strategy-more-link" className="group hidden sm:flex items-center gap-2 text-sm font-bold text-orange-400 hover:text-orange-300">الاستراتيجية كاملة <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" /></a>
        </div>
        <div className="grid md:grid-cols-2 gap-5 mb-5">
          {[['الرؤية', st.vision, Target], ['الرسالة', st.mission, Flag]].filter(([, t]) => t).map(([label, text, Icon], i) => (
            <motion.div key={label} {...reveal} transition={{ ...reveal.transition, delay: i * 0.1 }} data-testid={`strategy-${label === 'الرؤية' ? 'vision' : 'mission'}`} className="rounded-3xl bg-white/[0.06] border border-white/10 backdrop-blur-md p-7 lg:p-9">
              <div className="w-12 h-12 rounded-2xl bg-orange-500 flex items-center justify-center mb-5"><Icon className="w-6 h-6" /></div>
              <h3 className="text-xl font-black mb-3">{label}</h3>
              <p className="text-slate-300 leading-8">{text}</p>
            </motion.div>
          ))}
        </div>
        {st.values?.length > 0 && (
          <motion.div {...reveal} className="flex flex-wrap gap-3 mb-5">
            {st.values.map((v, i) => <span key={i} className="px-5 py-2.5 rounded-full bg-orange-500/15 border border-orange-500/30 text-orange-200 text-sm font-bold">{v}</span>)}
          </motion.div>
        )}
        {st.goals?.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {st.goals.slice(0, 4).map((g, i) => (
              <motion.div key={i} {...reveal} transition={{ ...reveal.transition, delay: i * 0.08 }} data-testid={`strategy-goal-${i}`} className="rounded-2xl bg-white/[0.04] border border-white/10 p-6 hover:bg-white/[0.08] transition-colors">
                <div className="text-3xl font-black text-orange-500/80 mb-3 tabular-nums">0{i + 1}</div>
                <h4 className="font-bold mb-2">{g.title}</h4>
                <p className="text-sm text-slate-400 leading-6 line-clamp-3">{g.description}</p>
              </motion.div>
            ))}
          </div>
        )}
        <a href="/strategy" data-testid="strategy-more-link-mobile" className="sm:hidden mt-6 flex items-center justify-center gap-2 text-sm font-bold text-orange-400">الاستراتيجية كاملة <ArrowLeft className="w-4 h-4" /></a>
      </div>
    </section>
  );
};

export const FeatureBoxes = ({ boxes }) => {
  if (!boxes?.length) return null;
  return (
    <section data-testid="feature-boxes" className="bg-white py-16 lg:py-24" dir="rtl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <SectionHead kicker="اكتشف خدماتنا" title="استكشف المزيد" />
        <div className={`grid gap-5 ${boxes.length > 2 ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
          {boxes.map((box, i) => {
            const c = box.background_color || '#F97316';
            return (
              <motion.a key={box.id || i} href={box.link} {...reveal} transition={{ ...reveal.transition, delay: i * 0.1 }} data-testid={`feature-box-${i}`}
                className="group relative overflow-hidden rounded-3xl bg-[#0B1220] p-8 lg:p-10 min-h-[300px] flex flex-col justify-between hover:-translate-y-1 transition-transform duration-300">
                <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full blur-3xl opacity-40 group-hover:opacity-60 transition-opacity duration-500" style={{ background: c }} />
                <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'repeating-linear-gradient(135deg, #fff 0 1px, transparent 1px 14px)' }} />
                <div className="absolute bottom-0 inset-x-0 h-1 origin-right scale-x-0 group-hover:scale-x-100 transition-transform duration-500" style={{ background: c }} />
                <div className="relative flex items-start justify-between">
                  <div className="w-20 h-20 rounded-2xl bg-white/10 border border-white/15 backdrop-blur-md flex items-center justify-center text-5xl group-hover:scale-110 group-hover:-rotate-6 transition-transform duration-300">{box.icon}</div>
                  <span className="text-6xl font-black text-white/10 tabular-nums">0{i + 1}</span>
                </div>
                <div className="relative mt-10">
                  <h3 className="text-2xl lg:text-3xl font-black text-white mb-2">{box.title}</h3>
                  {box.description && <p className="text-slate-300 leading-7 mb-6">{box.description}</p>}
                  <span className="inline-flex items-center gap-2 text-sm font-bold text-white px-5 py-2.5 rounded-full transition-colors" style={{ background: c }}>
                    اكتشف المزيد <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
                  </span>
                </div>
              </motion.a>
            );
          })}
        </div>
      </div>
    </section>
  );
};
